import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  FormHelperText,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getFormTypes, addEditEmailTemplate } from 'src/ApiServices';
import useMediaQuery from '@mui/material/useMediaQuery';

const EmailTemplateModal = ({ open, onClose, onSubmit, template, isEditing }) => {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    success: '',
    error: '',
    form: '',
    content: ''
  });
  const [error, setError] = useState('');
  const [bodyVariables, setBodyVariables] = useState([]);
  const [subjectVariables, setSubjectVariables] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const quillRef = useRef(null);
  const [formTypes, setFormTypes] = useState([]);
  const isMobile = useMediaQuery('(max-width:600px)');

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  const extractVariables = (text, useDollarSign = false) => {
    if (!text) return [];

    const pattern = /%([^%]+)%/g;
    const matches = text.match(pattern);
    if (!matches) return [];

    const cleanVariables = matches.map((match) => match.replace(/%/g, ''));

    const capitalVariables = cleanVariables.filter(
      (variable) => variable === variable.toUpperCase() && /^[A-Z_]+$/.test(variable)
    );

    return [...new Set(capitalVariables)];
  };

  const fetchFormTypes = async () => {
    try {
      const response = await getFormTypes();
      if (response.data && Array.isArray(response.data)) {
        setFormTypes(response.data);
      } else {
        setFormTypes([]);
      }
    } catch (error) {
      setFormTypes([]);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFormTypes();
      if (isEditing && template) {
        let templateContent = '';

        if (template.body) {
          try {
            if (typeof template.body === 'string') {
              const bodyData = JSON.parse(template.body);
              templateContent = bodyData[0]?.content || '';
            } else if (Array.isArray(template.body)) {
              templateContent = template.body[0]?.content || '';
            } else {
              templateContent = template.content || '';
            }
          } catch (error) {
            templateContent = template.content || '';
          }
        } else {
          templateContent = template.content || '';
        }
        const selectedFormType = formTypes.find((form) => form.name === template.form_type);
        const formId =
          template.form_type_id ||
          (template.form_type && typeof template.form_type === 'object'
            ? template.form_type.id
            : '');
        setFormData({
          name: template.name || '',
          subject: template.subject || '',
          success: template.success || '',
          error: template.error || '',
          form: formId,
          content: templateContent
        });

        setBodyVariables(extractVariables(templateContent, false));
        setSubjectVariables(extractVariables(template.subject, true));
      } else {
        setFormData({
          name: '',
          subject: '',
          success: '',
          error: '',
          form: '',
          content: ''
        });
        setBodyVariables([]);
        setSubjectVariables([]);
      }
      setError('');
      setFieldErrors({});
    }
  }, [open, isEditing, template]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (field === 'subject') {
      const extractedVars = extractVariables(value, true);
      setSubjectVariables(extractedVars);
    }

    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value
    }));

    const extractedVars = extractVariables(value, false);
    setBodyVariables(extractedVars);

    const contentError = validateField('content', value);
    setFieldErrors((prev) => ({
      ...prev,
      content: contentError
    }));
  };

  const insertSubjectVariable = (variable) => {
    const variableWithDollar = `$${variable}$`;
    const currentSubject = formData.subject;
    const newSubject = currentSubject + (currentSubject ? ' ' : '') + variableWithDollar;

    setFormData((prev) => ({
      ...prev,
      subject: newSubject
    }));

    const extractedVars = extractVariables(newSubject, true);
    setSubjectVariables(extractedVars);
  };

  const insertBodyVariable = (variable) => {
    const variableWithPercentage = `%${variable}%`;
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, variableWithPercentage);
        quill.setSelection(range.index + variableWithPercentage.length);
      } else {
        const length = quill.getLength();
        quill.insertText(length, variableWithPercentage);
        quill.setSelection(length + variableWithPercentage.length);
      }
      const newContent = quill.root.innerHTML;
      setFormData((prev) => ({
        ...prev,
        content: newContent
      }));
      const extractedVars = extractVariables(newContent, false);
      setBodyVariables(extractedVars);
    }
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return value.trim() ? '' : 'Name is required';
      case 'subject':
        return value.trim() ? '' : 'Subject is required';
      case 'content':
        const strippedValue = value?.replace(/<[^>]*>/g, '').trim();
        return strippedValue ? '' : 'Content is required';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const errors = {};

    errors.name = validateField('name', formData.name);
    errors.subject = validateField('subject', formData.subject);
    errors.content = validateField('content', formData.content);

    setFieldErrors(errors);

    return !Object.values(errors).some((error) => error !== '');
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const bodyData = JSON.stringify([
        {
          content: formData.content,
          type: 'Paragraph'
        }
      ]);
      const selectedFormType = formTypes.find(
        (form) => form.id.toString() === formData.form.toString()
      );
      const formTypeName = selectedFormType ? selectedFormType?.id : '';

      const submissionData = {
        name: formData.name,
        subject: formData.subject,
        success: formData.success,
        error: formData.error,
        form_type: formTypeName,
        body: bodyData
      };

      const response = await addEditEmailTemplate(isEditing ? template.id : null, submissionData);
      onSubmit(submissionData);
      handleClose();
    } catch (error) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(
          error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} template`
        );
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      subject: '',
      success: '',
      error: '',
      form: '',
      content: ''
    });
    setBodyVariables([]);
    setSubjectVariables([]);
    setError('');
    setFieldErrors({});
    onClose();
  };

  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.subject.trim() &&
      formData.content?.replace(/<[^>]*>/g, '').trim()
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1400px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          {isEditing ? 'Edit Email Template' : 'Add New Email Template'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark,
                  mb: 1
                }}
              >
                Template Name *
              </Typography>
              <TextField
                fullWidth
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
                placeholder="Enter template name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'poppinsRegular',
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark
                }}
              >
                Subject *
              </Typography>

              {subjectVariables.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'poppinsMedium',
                      color: palette.text.secondary,
                      mb: 1,
                      display: 'block'
                    }}
                  >
                    Variables in Subject:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {subjectVariables.map((variable) => (
                      <Chip
                        key={variable}
                        label={variable}
                        sx={{
                          backgroundColor: palette.success.light,
                          color: palette.success.contrastText
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <TextField
                fullWidth
                value={formData.subject}
                onChange={handleInputChange('subject')}
                error={!!fieldErrors.subject}
                helperText={fieldErrors.subject}
                placeholder="Enter email subject..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'poppinsRegular',
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: palette.primary.titledark
                }}
              >
                Use %VARIABLENAMEINCAPITAL% format for variavles. e.g (%NAME%, %EMAIL%,
                %COMPANYNAME%)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark,
                  mb: 1
                }}
              >
                Success Message
              </Typography>
              <TextField
                fullWidth
                value={formData.success}
                onChange={handleInputChange('success')}
                placeholder="Enter success message"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'poppinsRegular',
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark,
                  mb: 1
                }}
              >
                Error Message
              </Typography>
              <TextField
                fullWidth
                value={formData.error}
                onChange={handleInputChange('error')}
                placeholder="Enter error message"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'poppinsRegular',
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark,
                  mb: 1
                }}
              >
                Attach Form
              </Typography>
              <TextField
                select
                fullWidth
                value={formData.form}
                onChange={handleInputChange('form')}
                placeholder="Select a form"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'poppinsRegular',
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  }
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {formTypes.map((formType) => (
                  <MenuItem key={formType.id} value={formType.id}>
                    {formType.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {bodyVariables.length > 0 && (
            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark,
                  mb: 1
                }}
              >
                Body Content Variables
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {bodyVariables.map((variable) => (
                  <Chip
                    key={variable}
                    label={variable}
                    sx={{
                      backgroundColor: palette.success.light,
                      color: palette.success.contrastText,
                      '& .MuiChip-deleteIcon': {
                        color: palette.success.contrastText
                      }
                    }}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: palette.text.secondary,
                  mt: 1,
                  display: 'block'
                }}
              >
                Variables are automatically detected when you use %VARIABLE_NAME% format in body
                content
              </Typography>
            </Box>
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark,
              mb: 2,
              mt: 2
            }}
          >
            Email Template Content *
          </Typography>

          <Box
            sx={{
              border: fieldErrors.content
                ? `1px solid ${palette.error.main}`
                : `1px solid ${palette.primary.inputborder}`,
              borderRadius: 1,
              overflow: 'hidden',
              '& .ql-toolbar': {
                border: 'none',
                borderBottom: `1px solid ${palette.primary.inputborder}`,
                backgroundColor: palette.background.neutral,
                '& .ql-picker-label': {
                  fontFamily: 'poppinsRegular'
                },
                '& .ql-picker-options': {
                  fontFamily: 'poppinsRegular'
                }
              },
              '& .ql-container': {
                border: 'none',
                fontFamily: 'poppinsRegular',
                fontSize: '14px',
                minHeight: '400px'
              },
              '& .ql-editor': {
                minHeight: '400px',
                fontFamily: 'poppinsRegular',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontFamily: 'poppinsSemiBold',
                  marginTop: '8px',
                  marginBottom: '8px'
                },
                '& a': {
                  color: palette.primary.main,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: palette.primary.dark
                  }
                },
                '& img': {
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  maxWidth: '100%',
                  height: 'auto',
                  margin: '8px 0',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: `0 4px 12px ${palette.primary.main}40`
                  }
                },
                '&.ql-blank::before': {
                  color: palette.text.disabled,
                  fontStyle: 'normal',
                  fontFamily: 'poppinsRegular'
                }
              },
              '&:hover': {
                borderColor: palette.primary.main
              },
              '&:focus-within': {
                borderColor: palette.primary.main,
                borderWidth: 2
              }
            }}
          >
            <ReactQuill
              ref={quillRef}
              value={formData.content}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              placeholder="Enter email template content... "
              theme="snow"
            />
          </Box>

          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{
                color: palette.primary.titledark,
                mt: 1
              }}
            >
              Use %VARIABLENAMEINCAPITAL% format for variavles. e.g(%NAME%, %EMAIL%, %COMPANYNAME%)
            </Typography>
          </Grid>

          {fieldErrors.content && (
            <FormHelperText
              error
              sx={{
                ml: 0,
                mt: 1
              }}
            >
              {fieldErrors.content}
            </FormHelperText>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderColor: palette.primary.inputborder,
            color: palette.primary.title,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: palette.primary.hover
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid()}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            },
            '&:disabled': {
              backgroundColor: palette.primary.disabled,
              color: palette.text.disabled
            }
          }}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateModal;
